import { createFileRoute } from '@tanstack/react-router';
import { useEffect, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SquaredBackground } from '@/components/squared-background';
import { stripOAuthParamsFromLocation } from '@/utils/scrub-oauth-params';

/**
 * Browser fallback for the mobile app's OAuth redirect target.
 *
 * On a phone with the iPractice app installed this URL is a verified App Link
 * (Android) or Universal Link (iOS), so the OS opens the app and this page is
 * never rendered. It only shows when the link is opened on desktop, or on a
 * device where the app is missing or the domain association has not been
 * verified — which is exactly when the user is stuck and needs a way out.
 */
const MobileAuthFallback: FC = () => {
  const { t, i18n } = useTranslation();

  // B2C redirects here with `?code=…&state=…`. When the App Link is not
  // honoured the browser renders this page, so drop the credential from the
  // address bar before it reaches page-view telemetry or a referrer header.
  useEffect(() => {
    stripOAuthParamsFromLocation();
  }, []);

  const appStoreUrl = `https://apps.apple.com/nl/app/ipractice/id6737982953?l=${
    i18n.language === 'en' ? 'en-GB' : 'nl-NL'
  }`;
  const playStoreUrl = `https://play.google.com/store/apps/details?id=com.ipractice.myipractice&hl=${i18n.language}`;

  return (
    <div className="min-w-screen min-h-screen flex items-center justify-center relative isolate bg-background px-6">
      <SquaredBackground />
      <div className="relative z-10 w-full max-w-md text-center space-y-6">
        <h1 className="text-2xl font-semibold text-foreground">
          {t('mobile_auth_page.title')}
        </h1>
        <p className="text-muted-foreground">{t('mobile_auth_page.description')}</p>
        <p className="text-sm text-muted-foreground">{t('mobile_auth_page.getTheApp')}</p>
        <div className="flex items-center justify-center gap-4">
          <a href={appStoreUrl} target="_blank" rel="noreferrer">
            <img src="./appStore.svg" alt={t('mobile_auth_page.appStoreAlt')} className="h-10" />
          </a>
          <a href={playStoreUrl} target="_blank" rel="noreferrer">
            <img src="./playStore.png" alt={t('mobile_auth_page.playStoreAlt')} className="h-10" />
          </a>
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/mobile-auth')({
  component: MobileAuthFallback,
});
